/**
 * Handing a file to the browser as a download.
 *
 * Shared by everything that produces one — the PDF exports, the ExcaBook ZIP,
 * a single photo or sketch — so they all name, click and clean up the same way.
 *
 * ONE GRAMMAR for every export:
 * `{wann exportiert}-{Maßnahmennummer}_{Was}_{Wann}` (see
 * {@link composeFileName}), in plain ASCII (see {@link asciiFileName}) — a photo
 * adds its Lage to that head (see `features/media/mediaFileName`).
 *
 * The export moment leads because that is what a download folder sorts by, and
 * it is the one part every file has: two exports of the same Maßnahme land next
 * to each other instead of the second becoming "… (1)".
 */

import { isNativeApp, saveFileInApp } from '../lib/nativeApp';

/**
 * Download whatever `url` points at under `fileName`.
 *
 * The URL is the CALLER's: an object URL it already holds stays valid
 * afterwards, which is why nothing is revoked here. Use {@link downloadBlob}
 * for a blob with no life beyond the download.
 */
export function downloadUrl(url: string, fileName: string) {
    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    window.document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
}

/**
 * Download a blob under `fileName`, releasing its object URL afterwards.
 *
 * In the Android app there is no download to hand it to; the file is saved on
 * the device instead (see `lib/nativeApp`).
 */
export function downloadBlob(blob: Blob, fileName: string) {
    if (isNativeApp()) {
        void saveFileInApp(blob, fileName);
        return;
    }
    const url = URL.createObjectURL(blob);
    downloadUrl(url, fileName);
    URL.revokeObjectURL(url);
}

/** German letters that have a spelled-out form, which beats stripping them. */
const GERMAN_TRANSLITERATIONS: Record<string, string> = {
    ä: 'ae',
    ö: 'oe',
    ü: 'ue',
    Ä: 'Ae',
    Ö: 'Oe',
    Ü: 'Ue',
    ß: 'ss'
};

/**
 * One PART of a file name — no extension — reduced to plain ASCII:
 * `[A-Za-z0-9_-]` and nothing else. The dot goes too, so the caller's extension
 * stays the only one in the finished name.
 *
 * THE one sanitiser: every file this app hands out goes through it. Stricter
 * than Windows' forbidden characters, because these files leave the app — an
 * archive's ingest or a shell script handles "Bef_7000_Nordwand.jpg" better
 * than "Bef. 7000, Nordwand.jpg". Umlauts are SPELLED OUT rather than dropped
 * (ä→ae), because "Waelle" is still a word and "Wlle" is not; every other
 * accent loses its diacritic (é→e). Spaces and punctuation collapse into '_'.
 */
export function asciiFileName(name: string, fallback: string): string {
    // Spelled out BEFORE the decomposition below, which would otherwise turn ü
    // into a bare u and lose the spelling.
    const spelled = name.replace(
        /[äöüÄÖÜß]/g,
        (char) => GERMAN_TRANSLITERATIONS[char]
    );
    // Split the remaining accented letters into letter + combining mark, then
    // keep only what is ASCII — which drops those marks (é -> e).
    const ascii = Array.from(spelled.normalize('NFD'))
        .filter((char) => char.charCodeAt(0) < 128)
        .join('');
    const cleaned = ascii
        .replace(/[^A-Za-z0-9_-]+/g, '_')
        .replace(/_+/g, '_')
        .slice(0, 120)
        // After the cut, so a truncation landing mid-separator doesn't leave one
        // dangling.
        .replace(/^[_-]+|[_-]+$/g, '');
    return cleaned || fallback;
}

/**
 * WHEN a file was exported, as it is written at the head of its name:
 * "2026_08_11-14_35" — date parts joined by '_', the clock likewise, the two
 * separated by '-'.
 *
 * Local time, not UTC: this stamps the moment the person clicked export, and
 * that is the moment they will look for it under.
 *
 * To the minute by default. Two files made inside the same minute do collide,
 * and the browser resolves that its own way. `seconds` is for the files a person
 * does not choose the moment of: a rescue off a broken device can be triggered
 * twice in a row, and two of those must not land on one name.
 */
export function fileNameTimestamp(
    now: Date = new Date(),
    options: { seconds?: boolean } = {}
): string {
    const pad = (value: number) => String(value).padStart(2, '0');
    const date = [
        now.getFullYear(),
        pad(now.getMonth() + 1),
        pad(now.getDate())
    ].join('_');
    const clock = [pad(now.getHours()), pad(now.getMinutes())];
    if (options.seconds) {
        clock.push(pad(now.getSeconds()));
    }
    return `${date}-${clock.join('_')}`;
}

/**
 * Put the export moment in front of an otherwise finished name.
 *
 * Separate from {@link composeFileName} for the one caller whose head is not a
 * plain '_'-joined list: a photo carries its Lage behind a '+', which must
 * survive rather than be sanitised away a second time.
 */
export function stampFileName(name: string, now?: Date): string {
    return `${fileNameTimestamp(now)}-${name}`;
}

/**
 * The name an export downloads as, from its parts: the export moment first, then
 * the Maßnahmennummer, then WHAT the file is, then WHEN it covers —
 * "2026_08_11-14_35-M-2024-0123-1_Zeiterfassung_Mitarbeiter_KW25-2026.pdf".
 *
 * Every part is reduced to ASCII and empty ones are dropped, so a caller can
 * pass what it has without checking first.
 *
 * `extension` carries its dot ('.pdf').
 */
export function composeFileName(
    parts: ReadonlyArray<string | undefined>,
    extension: string
): string {
    const name = parts
        .map((part) => (part ? asciiFileName(part, '') : ''))
        .filter(Boolean)
        .join('_');
    return `${stampFileName(name)}${extension}`;
}

/**
 * The parts of a name saying WHICH records an export holds: the first and the
 * last of them, and — beyond two — how many there are.
 *
 * `labels` is expected in the order the document prints them, so its first and
 * last ARE the span: `['Bef-7000', 'Bef-7042', '12Befunde']`. Up to two the name
 * IS the whole set and needs no count.
 *
 * From three on the endpoints alone would lie: a ticked selection takes any rows
 * at all, so Bef. 7000 and Bef. 7042 with nothing between them would be named
 * exactly like the full forty-three, and the two would overwrite each other in a
 * folder. So the COUNT rides along; `plural` is the German noun it is counted in
 * ("Wochen", "Befunde", "Tage").
 *
 * A label the caller has nothing for drops out, and the count counts what is
 * left.
 */
export function fileNameSpan(
    labels: readonly string[],
    plural: string
): string[] {
    const named = labels.filter(Boolean);
    if (named.length === 0) {
        return [];
    }
    const parts = [named[0]];
    if (named.length > 1) {
        parts.push(named[named.length - 1]);
    }
    if (named.length > 2) {
        parts.push(`${named.length}${plural}`);
    }
    return parts;
}
